import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue";
const _hoisted_1 = { class: "flex h-full min-h-0 flex-col gap-4" };
const _hoisted_2 = { class: "flex flex-wrap items-center justify-between gap-3" };
const _hoisted_3 = { class: "text-lg font-semibold text-eblack-900" };
const _hoisted_4 = { class: "mt-1 text-sm text-eblack-500" };
const _hoisted_5 = ["disabled"];
const _hoisted_6 = {
    key: 0,
    class: "flex flex-wrap items-center justify-between gap-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600"
};
const _hoisted_7 = {
    key: 1,
    class: "rounded-lg border border-ewhite-200 bg-white p-6 text-sm text-eblack-500"
};
const _hoisted_8 = {
    key: 2,
    class: "min-h-0 flex-1 overflow-y-auto rounded-lg border border-ewhite-200 bg-white",
    "data-test": "notification-preferences-panel"
};
const _hoisted_9 = { class: "hidden grid-cols-[minmax(0,1fr)_minmax(220px,260px)_minmax(220px,260px)] gap-4 border-b border-ewhite-100 bg-ewhite-50 px-4 py-3 text-xs font-semibold uppercase text-eblack-500 lg:grid" };
const _hoisted_10 = { class: "min-w-0" };
const _hoisted_11 = { class: "text-sm font-semibold text-eblack-900" };
const _hoisted_12 = { class: "mt-1 text-xs text-eblack-500" };
const _hoisted_13 = ["data-test"];
const _hoisted_14 = { class: "flex items-center justify-between gap-3" };
const _hoisted_15 = { class: "text-sm font-medium text-eblack-700 lg:hidden" };
const _hoisted_16 = { class: "inline-flex cursor-pointer items-center gap-2" };
const _hoisted_17 = ["checked", "data-test", "onChange"];
const _hoisted_18 = { class: "relative h-5 w-9 rounded-full bg-ewhite-300 transition-colors peer-checked:bg-eorange-500" };
const _hoisted_19 = { class: "text-xs font-medium text-eblack-600" };
const _hoisted_20 = { class: "flex items-center gap-2" };
const _hoisted_21 = { class: "text-xs font-medium text-eblack-500" };
const _hoisted_22 = ["value", "data-test", "onChange"];
const _hoisted_23 = { value: "none" };
const _hoisted_24 = { value: "1h" };
const _hoisted_25 = { value: "24h" };
const _hoisted_26 = { value: "7d" };
const _hoisted_27 = {
    key: 0,
    value: "custom"
};
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";
import { useAuthStore } from "@/domains/auth";
import { useNotificationStore } from "../stores";
export default /*@__PURE__*/ _defineComponent({
    __name: 'NotificationPreferencesPanel',
    setup(__props) {
        const preferenceChannels = ["in_app", "email"];
        const preferenceEvents = [
            { type: "conversation.created", group: "conversation" },
            { type: "conversation.message.created", group: "conversation" },
            { type: "conversation.closed", group: "conversation" },
            { type: "conversation.invalidated", group: "conversation" },
            { type: "booking.created", group: "booking" },
            { type: "booking.updated", group: "booking" },
            { type: "booking.cancelled", group: "booking" },
            { type: "itinerary.approved", group: "itinerary" },
            { type: "itinerary.rejected", group: "itinerary" },
            { type: "review.submitted", group: "review" },
        ];
        const store = useNotificationStore();
        const authStore = useAuthStore();
        const { t, te, locale } = useI18n();
        const toast = useToast();
        const hasChanges = ref(false);
        const draft = ref({});
        const orderedPreferenceKeys = computed(() => preferenceEvents.flatMap((event) => preferenceChannels.map((channel) => preferenceKey(event.type, channel))));
        onMounted(() => {
            loadPreferences();
        });
        async function loadPreferences() {
            try {
                await store.fetchPreferences();
                hydrateDraft();
                hasChanges.value = false;
            }
            catch {
                // The store owns the displayed error state.
            }
        }
        async function save() {
            try {
                await store.savePreferences(preferencePayload());
                hydrateDraft();
                hasChanges.value = false;
                toast.success(t("notifications.preferences.saved"));
            }
            catch {
                toast.error(t("notifications.preferences.save_failed"));
            }
        }
        function hydrateDraft() {
            const nextDraft = {};
            for (const event of preferenceEvents) {
                for (const channel of preferenceChannels) {
                    const key = preferenceKey(event.type, channel);
                    const existing = effectivePreference(event.type, channel);
                    nextDraft[key] = {
                        event_type: event.type,
                        channel,
                        enabled: existing?.enabled ?? true,
                        muted_until: existing?.muted_until ?? null,
                    };
                }
            }
            draft.value = nextDraft;
        }
        function effectivePreference(eventType, channel) {
            const orgId = authStore.currentOrgId ? Number(authStore.currentOrgId) : null;
            const matchingPreferences = store.preferences.filter((preference) => preference.event_type === eventType && preference.channel === channel);
            return (matchingPreferences.find((preference) => preference.org_id === orgId) ??
                matchingPreferences.find((preference) => preference.org_id === null));
        }
        function draftPreference(eventType, channel) {
            const key = preferenceKey(eventType, channel);
            return (draft.value[key] ?? {
                event_type: eventType,
                channel,
                enabled: true,
                muted_until: null,
            });
        }
        function setEnabled(eventType, channel, enabled) {
            patchDraft(eventType, channel, { enabled });
        }
        function setEnabledFromEvent(eventType, channel, event) {
            const target = event.target;
            setEnabled(eventType, channel, target?.checked === true);
        }
        function setMuteWindow(eventType, channel, value) {
            if (value === "custom")
                return;
            const mutedUntil = value === "none" ? null : futureIsoFor(value);
            patchDraft(eventType, channel, { muted_until: mutedUntil });
        }
        function setMuteWindowFromEvent(eventType, channel, event) {
            const target = event.target;
            setMuteWindow(eventType, channel, target?.value ?? "none");
        }
        function patchDraft(eventType, channel, patch) {
            const key = preferenceKey(eventType, channel);
            draft.value = {
                ...draft.value,
                [key]: {
                    ...draftPreference(eventType, channel),
                    ...patch,
                },
            };
            hasChanges.value = true;
        }
        function preferencePayload() {
            return orderedPreferenceKeys.value.map((key) => {
                const preference = draft.value[key];
                return {
                    event_type: preference.event_type,
                    channel: preference.channel,
                    enabled: preference.enabled,
                    muted_until: preference.muted_until,
                };
            });
        }
        function preferenceKey(eventType, channel) {
            return `${eventType}:${channel}`;
        }
        function muteValue(eventType, channel) {
            return isMuted(eventType, channel) ? "custom" : "none";
        }
        function isMuted(eventType, channel) {
            const mutedUntil = draftPreference(eventType, channel).muted_until;
            return Boolean(mutedUntil && new Date(mutedUntil).getTime() > Date.now());
        }
        function mutedUntilLabel(eventType, channel) {
            const mutedUntil = draftPreference(eventType, channel).muted_until;
            if (!mutedUntil)
                return t("notifications.preferences.mute_options.none");
            const formatted = new Intl.DateTimeFormat(locale.value, {
                dateStyle: "medium",
                timeStyle: "short",
            }).format(new Date(mutedUntil));
            return t("notifications.preferences.mute_options.until", { date: formatted });
        }
        function futureIsoFor(value) {
            const now = Date.now();
            const durations = {
                "1h": 60 * 60 * 1000,
                "24h": 24 * 60 * 60 * 1000,
                "7d": 7 * 24 * 60 * 60 * 1000,
            };
            const duration = durations[value];
            return duration ? new Date(now + duration).toISOString() : null;
        }
        function eventLabel(eventType) {
            const key = `notifications.events.${eventType.replace(/\./g, "_")}.title`;
            return te(key) ? t(key) : eventType;
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("section", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("div", null, [
                        _createElementVNode("h2", _hoisted_3, _toDisplayString(_unref(t)("notifications.preferences.title")), 1),
                        _createElementVNode("p", _hoisted_4, _toDisplayString(_unref(t)("notifications.preferences.subtitle")), 1)
                    ]),
                    _createElementVNode("button", {
                        type: "button",
                        class: "inline-flex h-9 items-center rounded-lg bg-egreen-500 px-4 text-sm font-medium text-white disabled:opacity-60",
                        "data-test": "notification-preferences-save",
                        disabled: !hasChanges.value || _unref(store).isSavingPreferences,
                        onClick: save
                    }, _toDisplayString(_unref(store).isSavingPreferences
                        ? _unref(t)("notifications.preferences.saving")
                        : _unref(t)("notifications.preferences.save")), 9, _hoisted_5)
                ]),
                (_unref(store).preferencesError)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                        _createElementVNode("span", null, _toDisplayString(_unref(store).preferencesError), 1),
                        _createElementVNode("button", {
                            type: "button",
                            class: "rounded-md border border-red-200 px-2.5 py-1 font-medium text-red-700 hover:bg-red-100",
                            "data-test": "notification-preferences-retry",
                            onClick: loadPreferences
                        }, _toDisplayString(_unref(t)("notifications.preferences.retry")), 1)
                    ]))
                    : _createCommentVNode("", true),
                (_unref(store).isLoadingPreferences)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(_unref(t)("notifications.preferences.loading")), 1))
                    : (_openBlock(), _createElementBlock("div", _hoisted_8, [
                        _createElementVNode("div", _hoisted_9, [
                            _createElementVNode("span", null, _toDisplayString(_unref(t)("notifications.preferences.event")), 1),
                            _createElementVNode("span", null, _toDisplayString(_unref(t)("notifications.preferences.channels.in_app")), 1),
                            _createElementVNode("span", null, _toDisplayString(_unref(t)("notifications.preferences.channels.email")), 1)
                        ]),
                        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(preferenceEvents, (event) => {
                            return _createElementVNode("article", {
                                key: event.type,
                                class: "grid gap-4 border-b border-ewhite-100 px-4 py-4 last:border-b-0 lg:grid-cols-[minmax(0,1fr)_minmax(220px,260px)_minmax(220px,260px)]"
                            }, [
                                _createElementVNode("div", _hoisted_10, [
                                    _createElementVNode("p", _hoisted_11, _toDisplayString(eventLabel(event.type)), 1),
                                    _createElementVNode("p", _hoisted_12, _toDisplayString(_unref(t)(`notifications.preferences.groups.${event.group}`)), 1)
                                ]),
                                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(preferenceChannels, (channel) => {
                                    return _createElementVNode("div", {
                                        key: `${event.type}:${channel}`,
                                        class: "flex flex-col gap-2 rounded-md border border-ewhite-100 bg-ewhite-50/70 p-3 lg:border-0 lg:bg-transparent lg:p-0",
                                        "data-test": `notification-preference-${event.type}-${channel}`
                                    }, [
                                        _createElementVNode("div", _hoisted_14, [
                                            _createElementVNode("span", _hoisted_15, _toDisplayString(_unref(t)(`notifications.preferences.channels.${channel}`)), 1),
                                            _createElementVNode("label", _hoisted_16, [
                                                _createElementVNode("input", {
                                                    type: "checkbox",
                                                    class: "peer sr-only",
                                                    checked: draftPreference(event.type, channel).enabled,
                                                    "data-test": `notification-preference-toggle-${event.type}-${channel}`,
                                                    onChange: ($event) => (setEnabledFromEvent(event.type, channel, $event))
                                                }, null, 40, _hoisted_17),
                                                _createElementVNode("span", _hoisted_18, [
                                                    _createElementVNode("span", {
                                                        class: _normalizeClass(["absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white transition-transform",
                                                            draftPreference(event.type, channel).enabled
                                                                ? 'translate-x-4'
                                                                : 'translate-x-0'
                                                        ])
                                                    }, null, 2)
                                                ]),
                                                _createElementVNode("span", _hoisted_19, _toDisplayString(draftPreference(event.type, channel).enabled
                                                    ? _unref(t)("notifications.preferences.enabled")
                                                    : _unref(t)("notifications.preferences.disabled")), 1)
                                            ])
                                        ]),
                                        _createElementVNode("label", _hoisted_20, [
                                            _createElementVNode("span", _hoisted_21, _toDisplayString(_unref(t)("notifications.preferences.mute")), 1),
                                            _createElementVNode("select", {
                                                class: "h-8 min-w-0 flex-1 rounded-md border border-ewhite-200 bg-white px-2 text-xs text-eblack-700",
                                                value: muteValue(event.type, channel),
                                                "data-test": `notification-preference-mute-${event.type}-${channel}`,
                                                onChange: ($event) => (setMuteWindowFromEvent(event.type, channel, $event))
                                            }, [
                                                _createElementVNode("option", _hoisted_23, _toDisplayString(_unref(t)("notifications.preferences.mute_options.none")), 1),
                                                _createElementVNode("option", _hoisted_24, _toDisplayString(_unref(t)("notifications.preferences.mute_options.one_hour")), 1),
                                                _createElementVNode("option", _hoisted_25, _toDisplayString(_unref(t)("notifications.preferences.mute_options.one_day")), 1),
                                                _createElementVNode("option", _hoisted_26, _toDisplayString(_unref(t)("notifications.preferences.mute_options.one_week")), 1),
                                                (isMuted(event.type, channel))
                                                    ? (_openBlock(), _createElementBlock("option", _hoisted_27, _toDisplayString(mutedUntilLabel(event.type, channel)), 1))
                                                    : _createCommentVNode("", true)
                                            ], 40, _hoisted_22)
                                        ])
                                    ], 8, _hoisted_13);
                                }), 64))
                            ]);
                        }), 64))
                    ]))
            ]));
        };
    }
});
